import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { ExtendedBorderLine } from '@/components/extended-border-line'
import { SQUIRCLE_CORNER_SEAL_SIZE_PX } from '@/components/squircle-cell-frame'
import { SquircleCornerBox } from '@/components/squircle-corner-box'
import { SunriseOverlay } from '@/components/sunrise-overlay'
import { VerticalBorderLine } from '@/components/vertical-border-line'
import {
  getInTouchOverlayBorderDrawAnimation,
  getInTouchOverlayBorderNames,
} from '@/data/get-in-touch-overlay-borders'
import { contactChannels } from '@/data/morning-landing-content'
import { cn } from '@/lib/utils'

const QuickContactForm = lazy(() =>
  import('@/components/quick-contact-form').then((m) => ({
    default: m.QuickContactForm,
  })),
)

const detailsColumnBorderClass = 'z-11'
const overlayBorderLineClass = cn('bg-cream/30', detailsColumnBorderClass)
const overlayBorderStrokeClass = 'stroke-cream/30'
const overlayBleedBorderClass = 'bg-cream/30'
const overlayCornerFillClass = 'bg-ember dark:bg-ember-deep'

const detailsColumnTopBleedLeftAnimation =
  getInTouchOverlayBorderDrawAnimation[
    getInTouchOverlayBorderNames.detailsColumnTopBleedLeft
  ]
const detailsColumnTopEdgeAnimation =
  getInTouchOverlayBorderDrawAnimation[
    getInTouchOverlayBorderNames.detailsColumnTopEdge
  ]
const detailsColumnTopRightCornerAnimation =
  getInTouchOverlayBorderDrawAnimation[
    getInTouchOverlayBorderNames.detailsColumnTopRightCorner
  ]
const detailsColumnColumnDividerAnimation =
  getInTouchOverlayBorderDrawAnimation[
    getInTouchOverlayBorderNames.detailsColumnColumnDivider
  ]
const formColumnBottomEdgeAnimation =
  getInTouchOverlayBorderDrawAnimation[
    getInTouchOverlayBorderNames.formColumnBottomEdge
  ]
const formColumnBottomLeftCornerAnimation =
  getInTouchOverlayBorderDrawAnimation[
    getInTouchOverlayBorderNames.formColumnBottomLeftCorner
  ]
const formColumnBottomBleedRightAnimation =
  getInTouchOverlayBorderDrawAnimation[
    getInTouchOverlayBorderNames.formColumnBottomBleedRight
  ]

const headingFontClass = 'font-heading-outfit'

const MOBILE_NAV_MAX_WIDTH_MQ = '(max-width: 1023px)'

type MobileContactOpener = () => void

type GetInTouchOverlayContextValue = {
  isOpen: boolean
  open: (source?: HTMLButtonElement | null) => void
  /** Site header registers this so mobile CTAs open the menu contact sheet. */
  registerMobileContactOpener: (opener: MobileContactOpener | null) => void
}

const GetInTouchOverlayContext = createContext<GetInTouchOverlayContextValue | null>(
  null,
)

export function useGetInTouchOverlay() {
  const context = useContext(GetInTouchOverlayContext)
  if (!context) {
    throw new Error('useGetInTouchOverlay must be used within GetInTouchOverlayProvider')
  }
  return context
}

type GetInTouchSunOverlayProps = {
  onClosed: () => void
}

function GetInTouchSunOverlay({ onClosed }: GetInTouchSunOverlayProps) {
  return (
    <SunriseOverlay
      onClosed={onClosed}
      labelledBy="get-in-touch-heading"
      closeAriaLabel="Close contact form"
      autoFocusSelector="input"
      panelClassName="my-auto max-w-5xl"
    >
      {({ screenFilled: bordersInView, animateBorders }) => (
        <div className="grid items-stretch gap-10 lg:grid-cols-2 lg:gap-0">
          <article className="relative flex h-full flex-col">
            <div className="relative flex h-full min-h-full flex-col">
              <div className="hidden lg:contents">
                <ExtendedBorderLine
                  borderName={
                    getInTouchOverlayBorderNames.detailsColumnTopBleedLeft
                  }
                  edge="top"
                  direction="left"
                  drawDirection={
                    detailsColumnTopBleedLeftAnimation.drawDirection
                  }
                  lineClassName={overlayBorderLineClass}
                  inView={bordersInView}
                  animateDraw={animateBorders}
                  drawDelayMs={detailsColumnTopBleedLeftAnimation.drawDelayMs}
                  drawDurationMs={
                    detailsColumnTopBleedLeftAnimation.drawDurationMs
                  }
                />
                <ExtendedBorderLine
                  borderName={getInTouchOverlayBorderNames.detailsColumnTopEdge}
                  edge="top"
                  direction="left"
                  endInsetPx={SQUIRCLE_CORNER_SEAL_SIZE_PX}
                  drawDirection={detailsColumnTopEdgeAnimation.drawDirection}
                  lineClassName={overlayBorderLineClass}
                  inView={bordersInView}
                  animateDraw={animateBorders}
                  drawDelayMs={detailsColumnTopEdgeAnimation.drawDelayMs}
                  drawDurationMs={detailsColumnTopEdgeAnimation.drawDurationMs}
                />
                <SquircleCornerBox
                  borderName={
                    getInTouchOverlayBorderNames.detailsColumnTopRightCorner
                  }
                  corner="top-right"
                  bgClassName={overlayCornerFillClass}
                  strokeClassName={overlayBorderStrokeClass}
                  className={detailsColumnBorderClass}
                  grain
                  animateDraw={animateBorders}
                  inView={bordersInView}
                  drawDelayMs={
                    detailsColumnTopRightCornerAnimation.drawDelayMs
                  }
                  drawDurationMs={
                    detailsColumnTopRightCornerAnimation.drawDurationMs
                  }
                />
                <VerticalBorderLine
                  borderName={
                    getInTouchOverlayBorderNames.detailsColumnColumnDivider
                  }
                  side="right"
                  topOffset={SQUIRCLE_CORNER_SEAL_SIZE_PX}
                  bottomOffset={SQUIRCLE_CORNER_SEAL_SIZE_PX}
                  lineClassName={overlayBorderLineClass}
                  inView={bordersInView}
                  animateDraw={animateBorders}
                  drawDelayMs={
                    detailsColumnColumnDividerAnimation.drawDelayMs
                  }
                  drawDurationMs={
                    detailsColumnColumnDividerAnimation.drawDurationMs
                  }
                />
              </div>
              <div
                aria-hidden
                className={cn(
                  'pointer-events-none absolute inset-x-0 top-0 h-px lg:hidden',
                  overlayBleedBorderClass,
                )}
              />
              <div className="sunrise-overlay__content get-in-touch-overlay__content relative z-20 flex flex-col px-0 pt-0 pb-2 lg:px-10 lg:pt-16 lg:pb-16 lg:pr-12">
                <h2
                  id="get-in-touch-heading"
                  className={cn(
                    'max-w-[40ch] text-3xl font-semibold tracking-tight text-balance text-paper sm:text-4xl',
                    headingFontClass,
                  )}
                >
                  We want to hear about your next project
                </h2>
                <p
                  className={cn(
                    'mt-5 max-w-[52ch] text-pretty text-sm text-paper/50',
                    headingFontClass,
                  )}
                >
                  Arrange a meeting to discuss:
                </p>
                <div className="mt-10 space-y-6">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-paper/50">
                      Email
                    </p>
                    <a
                      href={contactChannels.email.href}
                      className={cn(
                        'mt-1 inline-block text-lg font-semibold text-paper/80 transition-colors hover:text-paper',
                        headingFontClass,
                      )}
                    >
                      {contactChannels.email.display}
                    </a>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-paper/50">
                      Phone
                    </p>
                    <a
                      href={contactChannels.phone.href}
                      className={cn(
                        'mt-1 inline-block text-lg font-semibold text-paper/80 transition-colors hover:text-paper',
                        headingFontClass,
                      )}
                    >
                      {contactChannels.phone.display}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <article className="relative flex h-full flex-col lg:-ml-px">
            <div className="relative flex h-full min-h-full flex-col">
              <div className="hidden lg:contents">
                <ExtendedBorderLine
                  borderName={getInTouchOverlayBorderNames.formColumnBottomEdge}
                  edge="bottom"
                  direction="right"
                  startInsetPx={SQUIRCLE_CORNER_SEAL_SIZE_PX}
                  drawDirection={formColumnBottomEdgeAnimation.drawDirection}
                  lineClassName={overlayBorderLineClass}
                  inView={bordersInView}
                  animateDraw={animateBorders}
                  drawDelayMs={formColumnBottomEdgeAnimation.drawDelayMs}
                  drawDurationMs={formColumnBottomEdgeAnimation.drawDurationMs}
                />
                <SquircleCornerBox
                  borderName={
                    getInTouchOverlayBorderNames.formColumnBottomLeftCorner
                  }
                  corner="bottom-left"
                  bgClassName={overlayCornerFillClass}
                  strokeClassName={overlayBorderStrokeClass}
                  className={detailsColumnBorderClass}
                  grain
                  animateDraw={animateBorders}
                  inView={bordersInView}
                  drawDelayMs={formColumnBottomLeftCornerAnimation.drawDelayMs}
                  drawDurationMs={
                    formColumnBottomLeftCornerAnimation.drawDurationMs
                  }
                />
                <ExtendedBorderLine
                  borderName={
                    getInTouchOverlayBorderNames.formColumnBottomBleedRight
                  }
                  edge="bottom"
                  direction="right"
                  lineClassName={overlayBorderLineClass}
                  inView={bordersInView}
                  animateDraw={animateBorders}
                  drawDelayMs={
                    formColumnBottomBleedRightAnimation.drawDelayMs
                  }
                  drawDurationMs={
                    formColumnBottomBleedRightAnimation.drawDurationMs
                  }
                />
              </div>
              <div
                aria-hidden
                className={cn(
                  'pointer-events-none absolute inset-x-0 bottom-0 h-px lg:hidden',
                  overlayBleedBorderClass,
                )}
              />
              <div className="sunrise-overlay__content get-in-touch-overlay__content relative z-20 flex flex-col px-0 pt-0 pb-2 lg:px-10 lg:pt-16 lg:pb-16 lg:pl-12">
                <Suspense fallback={null}>
                  <QuickContactForm
                    idPrefix="overlay-contact"
                    variant="overlay"
                  />
                </Suspense>
              </div>
            </div>
          </article>
        </div>
      )}
    </SunriseOverlay>
  )
}

export function GetInTouchOverlayProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isRendered, setIsRendered] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const mobileContactOpenerRef = useRef<MobileContactOpener | null>(null)

  const registerMobileContactOpener = useCallback(
    (opener: MobileContactOpener | null) => {
      mobileContactOpenerRef.current = opener
    },
    [],
  )

  const open = useCallback((source?: HTMLButtonElement | null) => {
    if (source) {
      triggerRef.current = source
    }

    // Below `lg`, prefer the mobile menu contact sheet over the desktop popup.
    const openMobileContact = mobileContactOpenerRef.current
    if (
      openMobileContact &&
      typeof window !== 'undefined' &&
      window.matchMedia(MOBILE_NAV_MAX_WIDTH_MQ).matches
    ) {
      openMobileContact()
      return
    }

    setIsRendered(true)
    setIsOpen(true)
  }, [])

  // Deep-link: `/contact` and `/contact/` open the Get in touch popup, then
  // normalize the URL to `/` so the address bar stays clean.
  useEffect(() => {
    const pathname = window.location.pathname.replace(/\/+$/, '') || '/'
    if (pathname !== '/contact') return

    window.history.replaceState(
      window.history.state,
      '',
      `/${window.location.search}${window.location.hash}`,
    )
    open()
  }, [open])

  const handleClosed = useCallback(() => {
    setIsRendered(false)
    setIsOpen(false)
    requestAnimationFrame(() => {
      triggerRef.current?.focus()
    })
  }, [])

  return (
    <GetInTouchOverlayContext.Provider
      value={{ isOpen, open, registerMobileContactOpener }}
    >
      {children}
      {isRendered ? <GetInTouchSunOverlay onClosed={handleClosed} /> : null}
    </GetInTouchOverlayContext.Provider>
  )
}
